import { ComponentType, lazy, Suspense } from "react";
import { createBrowserRouter, Navigate, RouteObject } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AppLayout } from "@/layouts/AppLayout";
import { ProtectedRoute } from "@/auth/ProtectedRoute";
import { LoginPage } from "@/pages/LoginPage";

// Route-basiertes Code-Splitting: Jede Seite wird als eigener Chunk erst beim
// Aufruf geladen (schwere Abhängigkeiten wie react-pdf landen so nicht im
// Haupt-Bundle). Die Seiten nutzen Named Exports, daher das Mapping auf default.
//
// Bei einem neuen Deployment ändern sich die Chunk-Hashes. Hatte ein User die
// alte index.html noch gecacht, schlägt der dynamische Import fehl (Server liefert
// index.html statt JS → MIME-Fehler). In dem Fall wird die Seite einmalig neu
// geladen, um die aktuelle index.html mit den richtigen Chunk-Referenzen zu holen.
function lazyPage<T extends Record<string, ComponentType>>(
  loader: () => Promise<T>,
  name: keyof T
) {
  const Component = lazy(() =>
    loader()
      .then((m) => ({ default: m[name] as ComponentType }))
      .catch((err: unknown) => {
        // Nur einmal pro Session neuladen, um Endlos-Schleifen zu vermeiden.
        const reloadKey = `chunk-reload:${String(name)}`;
        if (!sessionStorage.getItem(reloadKey)) {
          sessionStorage.setItem(reloadKey, "1");
          window.location.reload();
        }
        throw err;
      })
  );
  return (
    <Suspense
      fallback={
        <div className="flex items-center gap-2 p-6 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Lade …
        </div>
      }
    >
      <Component />
    </Suspense>
  );
}

const routes: RouteObject[] = [
  {
    path: "/login",
    element: <LoginPage />,
  },
  {
    element: (
      <ProtectedRoute>
        <AppLayout />
      </ProtectedRoute>
    ),
    children: [
      { index: true, element: lazyPage(() => import("@/pages/DashboardPage"), "DashboardPage") },
      { path: "documents", element: lazyPage(() => import("@/pages/DocumentsPage"), "DocumentsPage") },
      { path: "documents/:id", element: lazyPage(() => import("@/pages/DocumentDetailPage"), "DocumentDetailPage") },
      { path: "postbox/new", element: lazyPage(() => import("@/pages/PostboxNewPage"), "PostboxNewPage") },
      { path: "postbox/recognized", element: lazyPage(() => import("@/pages/PostboxRecognizedPage"), "PostboxRecognizedPage") },
      { path: "todos", element: lazyPage(() => import("@/pages/TodosPage"), "TodosPage") },
      { path: "views", element: lazyPage(() => import("@/pages/ViewsPage"), "ViewsPage") },
      { path: "settings", element: lazyPage(() => import("@/pages/SettingsPage"), "SettingsPage") },
      { path: "stammdaten", element: lazyPage(() => import("@/pages/StammdatenPage"), "StammdatenPage") },
      { path: "scan-environments", element: lazyPage(() => import("@/pages/ScanEnvironmentsPage"), "ScanEnvironmentsPage") },
      { path: "scan-environments/:id", element: lazyPage(() => import("@/pages/ScanEnvironmentDetailPage"), "ScanEnvironmentDetailPage") },
      { path: "filesystem", element: lazyPage(() => import("@/pages/FilesystemBrowserPage"), "FilesystemBrowserPage") },
      { path: "banking-import", element: lazyPage(() => import("@/pages/BankingImportPage"), "BankingImportPage") },
      { path: "bank-suggestions", element: lazyPage(() => import("@/pages/AssistantPage"), "AssistantPage") },
      { path: "auswertungen", element: lazyPage(() => import("@/pages/MietkontoPage"), "MietkontoPage") },
      { path: "vertraege", element: lazyPage(() => import("@/pages/ContractsPage"), "ContractsPage") },
      { path: "admin/users", element: lazyPage(() => import("@/pages/AdminUsersPage"), "AdminUsersPage") },
    ],
  },
  {
    path: "advisor/:token",
    element: lazyPage(() => import("@/pages/AdvisorPortalPage"), "AdvisorPortalPage"),
  },
  { path: "*", element: <Navigate to="/" replace /> },
];

export const router = createBrowserRouter(routes);
